import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Reservation, ReservationStatus } from '../../core/model/reservation';
import { canModify, cancellability } from '../../core/model/reservation';
import { addressSingleLine } from '../../core/model/restaurant';
import { Formatters } from '../../core/util/formatters';
import { PhoneNormalizer } from '../../core/util/phoneNormalizer';
import { nowDateTime } from '../../core/util/datetime';
import { S } from '../../i18n/strings';
import { useContainer } from '../../state/ContainerContext';
import { useAsync } from '../../state/useAsync';
import { Dialog } from '../components/Dialog';
import { ErrorBanner, SkeletonBlock, StateContainer } from '../components/StateContainer';
import type { AppError } from '../../core/error/appError';

export function statusLabel(status: ReservationStatus): string {
  switch (status) {
    case 'CONFIRMED':
      return S.reservationStatusConfirmed;
    case 'CANCELLED':
      return S.reservationStatusCancelled;
    case 'COMPLETED':
      return S.reservationStatusCompleted;
    case 'NO_SHOW':
      return S.reservationStatusNoShow;
  }
}

/**
 * Detalle de una reserva, con las acciones que de verdad aplican.
 *
 * "Modificar" no se renderiza cuando no aplica; "Cancelar" sí se renderiza pero deshabilitado y
 * con el motivo visible cuando la política lo bloquea, para que el usuario entienda por qué no
 * puede en lugar de buscar un botón que desapareció.
 */
export function ReservationDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { reservations } = useContainer();
  const [confirming, setConfirming] = useState(false);
  const [cancelError, setCancelError] = useState<AppError | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const { state, reload } = useAsync(() => reservations.get(id), [reservations, id]);
  const now = useMemo(() => nowDateTime(), []);

  const cancel = async (reservation: Reservation) => {
    setCancelling(true);
    const result = await reservations.cancel(reservation.id);
    setCancelling(false);
    setConfirming(false);
    if (result.ok) reload();
    else setCancelError(result.error);
  };

  return (
    <StateContainer state={state} loading={<SkeletonBlock height={220} />} onRetry={reload}>
      {(reservation) => {
        const address = addressSingleLine(reservation.address);
        const cancelState = cancellability(reservation, now);

        return (
          <div className="stack stack--loose">
            {cancelError && <ErrorBanner error={cancelError} onRetry={() => cancel(reservation)} />}

            <section className="stack stack--tight">
              <span className={`status-chip status-chip--${reservation.status.toLowerCase()}`}>
                {statusLabel(reservation.status)}
              </span>
              <h1>{reservation.restaurantName}</h1>
              <p className="confirmation-code">{S.confirmationCode(reservation.confirmationCode)}</p>
            </section>

            <div className="card" style={{ padding: 16 }}>
              <dl className="summary">
                <dt className="summary__key">{S.pickerDate}</dt>
                <dd className="summary__value">{Formatters.longDate(reservation.date)}</dd>
                <dt className="summary__key">{S.pickerTime}</dt>
                <dd className="summary__value">{Formatters.time(reservation.time)}</dd>
                <dt className="summary__key">{S.pickerParty}</dt>
                <dd className="summary__value">{Formatters.partySize(reservation.partySize)}</dd>
                {reservation.guest.fullName && (
                  <>
                    <dt className="summary__key">{S.reservationGuest}</dt>
                    <dd className="summary__value">{reservation.guest.fullName}</dd>
                  </>
                )}
                {reservation.guest.phone && (
                  <>
                    <dt className="summary__key">{S.bookingPhone}</dt>
                    <dd className="summary__value">{PhoneNormalizer.format(reservation.guest.phone)}</dd>
                  </>
                )}
                {reservation.occasion && (
                  <>
                    <dt className="summary__key">{S.reservationOccasion}</dt>
                    <dd className="summary__value">{reservation.occasion}</dd>
                  </>
                )}
                {reservation.notes && (
                  <>
                    <dt className="summary__key">{S.reservationNotes}</dt>
                    <dd className="summary__value">{reservation.notes}</dd>
                  </>
                )}
                {address && (
                  <>
                    <dt className="summary__key">Dirección</dt>
                    <dd className="summary__value">{address}</dd>
                  </>
                )}
              </dl>
            </div>

            <div className="row">
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => navigate(`/restaurante/${reservation.restaurantId}`)}
              >
                {S.reservationViewRestaurant}
              </button>

              {/* La acción no existe en una reserva pasada o cancelada: no se dibuja. */}
              {canModify(reservation, now) && (
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() =>
                    navigate(`/reservar/${reservation.restaurantId}?reserva=${reservation.id}`)
                  }
                >
                  {S.reservationModify}
                </button>
              )}

              {cancelState.kind !== 'NOT_APPLICABLE' && (
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={cancelState.kind === 'BLOCKED_BY_POLICY' || cancelling}
                  title={
                    cancelState.kind === 'BLOCKED_BY_POLICY'
                      ? S.reservationCancelNotAllowed(cancelState.hours)
                      : undefined
                  }
                  onClick={() => setConfirming(true)}
                >
                  {S.reservationCancel}
                </button>
              )}
            </div>

            {/* El motivo se muestra siempre junto al botón bloqueado, no solo en el tooltip. */}
            {cancelState.kind === 'BLOCKED_BY_POLICY' && (
              <p className="muted">{S.reservationCancelNotAllowed(cancelState.hours)}</p>
            )}

            {confirming && (
              <Dialog
                title={S.reservationCancelConfirmTitle}
                onClose={() => setConfirming(false)}
                footer={
                  <>
                    <button type="button" className="btn btn--text" onClick={() => setConfirming(false)}>
                      {S.reservationCancelConfirmNo}
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={cancelling}
                      onClick={() => cancel(reservation)}
                    >
                      {cancelling ? S.loading : S.reservationCancelConfirmYes}
                    </button>
                  </>
                }
              >
                <p>{S.reservationCancelConfirmBody(reservation.restaurantName)}</p>
              </Dialog>
            )}
          </div>
        );
      }}
    </StateContainer>
  );
}
